import React from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Link,
  Typography,
  Container,
  ButtonBase,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PublicIcon from "@mui/icons-material/Public";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { Link as RouterLink } from "react-router-dom";
import { Fade, Slide } from "react-awesome-reveal";

const features = [
  {
    title: "Gestioná todas tus reservas",
    description:
      "Calendario único para tarifas, cupos y disponibilidad. Todo sincronizado: sin dobles reservas.",
    links: [
      { text: "Gestor de Canales", href: "/channel-manager" },
      { text: "Motor de Reservas", href: "/booking-engine" },
      { text: "Panel Administrativo", href: "/admin-panel" },
    ],
    icon: <CalendarTodayIcon sx={{ color: "#903AF2" }} />,
    solapaColor: "#EE964B",
  },
  {
    title: "Conseguí más huéspedes",
    description:
      "Conectá tu alojamiento a los principales portales y multiplicá la visibilidad de tus fechas clave.",
    links: [],
    icon: <PublicIcon sx={{ color: "#903AF2" }} />,
    solapaColor: "#EAF7CF",
  },
  {
    title: "Automatizá tareas clave",
    description:
      "Confirmaciones, cobros y recordatorios automáticos. Menos tareas repetitivas, más tiempo para tu huésped.",
    links: [
      { text: "Automatización", href: "/automation" },
      { text: "Pagos Online", href: "/payments" },
      { text: "Atención al Cliente", href: "/customer-support" },
    ],
    icon: <FlashOnIcon sx={{ color: "#903AF2" }} />,
    solapaColor: "#D1C4E9",
  },
  {
    title: "Escalá tu alojamiento",
    description:
      "Sumá propiedades y herramientas sin cambiar de sistema. Ubika crece con vos.",
    links: [],
    icon: <RocketLaunchIcon sx={{ color: "#903AF2" }} />,
    solapaColor: "#B2DFDB",
  },
];

export default function FeaturesSection() {
  return (
    <Box
      sx={{
        position: "relative",
        py: { xs: 6, md: 8 },
        // ✅ mismo gradiente del Hero2
        background:
          "linear-gradient(180deg, #ffffff 0%, #efe7ff 40%, #903AF2 100%)",
        overflow: "hidden",
      }}
    >
      {/* glow sutil para profundidad */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "radial-gradient(900px 420px at 18% 10%, rgba(255,255,255,0.55), rgba(255,255,255,0) 60%), radial-gradient(900px 420px at 82% 20%, rgba(255,255,255,0.25), rgba(255,255,255,0) 60%)",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          textAlign="center"
          mb={{ xs: 5, md: 7 }}
          maxWidth="900px"
          mx="auto"
        >
          <Slide direction="up" triggerOnce>
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                color: "#1d1d1f",
                fontFamily: "'Red Hat Display', sans-serif",
                mb: 1,
                letterSpacing: -0.2,
              }}
            >
              ¿Qué incluye Ubika?
            </Typography>
          </Slide>

          <Fade triggerOnce delay={120}>
            <Typography
              variant="body1"
              sx={{
                color: "rgba(0,0,0,0.72)",
                fontFamily: "'Merriweather Sans', sans-serif",
                maxWidth: 780,
                mx: "auto",
              }}
            >
              Todo lo que tu alojamiento necesita para crecer, centralizar y
              simplificar su gestión.
            </Typography>
          </Fade>

          {/* divider más pro con colores Ubika */}
          <Box
            sx={{
              mx: "auto",
              mt: 2.2,
              width: { xs: 140, md: 180 },
              height: 6,
              borderRadius: 999,
              background:
                "linear-gradient(90deg, #EE964B 0%, #FFE735 35%, #EAF7CF 100%)",
              boxShadow: "0 12px 24px rgba(0,0,0,0.12)",
            }}
          />
        </Box>

        <Grid container spacing={{ xs: 3, md: 4 }} justifyContent="center">
          {features.map((feature, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              key={index}
              sx={{ display: "flex" }}
            >
              <Fade triggerOnce fraction={0.15} delay={index * 80}>
                <Box
                  sx={{
                    width: "100%",
                    position: "relative",
                    "&:hover .lift": { transform: "translateY(-6px)" },
                  }}
                >
                  {/* Card clic-completa con foco visible */}
                  <ButtonBase
                    className="lift"
                    focusRipple
                    sx={{
                      textAlign: "left",
                      borderRadius: 4,
                      outline: "none",
                      width: "100%",
                      transition: "transform .22s ease",
                      "&:focus-visible": {
                        boxShadow: "0 0 0 3px #FFE735",
                        borderRadius: 4,
                      },
                    }}
                  >
                    <Card
                      elevation={0}
                      sx={{
                        width: "100%",
                        borderRadius: 4,
                        overflow: "hidden",
                        position: "relative",
                        background:
                          "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.78) 100%)",
                        backdropFilter: "blur(8px)",
                        border: "1px solid rgba(255,255,255,0.55)",
                        boxShadow:
                          "0 18px 46px rgba(0,0,0,0.12), 0 0 28px rgba(255,255,255,0.65)",
                      }}
                    >
                      {/* Accent superior (mejor que “solapa” lateral) */}
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          pointerEvents: "none",
                          background: `linear-gradient(90deg, ${feature.solapaColor} 0%, rgba(255,255,255,0) 55%)`,
                          opacity: 0.18,
                        }}
                      />

                      {/* Borde/stripe lateral fino */}
                      <Box
                        sx={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 8,
                          backgroundColor: feature.solapaColor,
                        }}
                      />

                      <CardContent
                        sx={{
                          p: { xs: 3, md: 4 },
                          pl: { xs: 3.5, md: 4.2 },
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.25,
                          minHeight: { xs: 220, md: 240 },
                        }}
                      >
                        {/* icon badge */}
                        <Box
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 3,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                              "linear-gradient(180deg, rgba(144,58,242,0.10) 0%, rgba(144,58,242,0.04) 100%)",
                            border: "1px solid rgba(144,58,242,0.14)",
                            boxShadow: "0 10px 18px rgba(0,0,0,0.08)",
                            mb: 0.5,
                          }}
                        >
                          {feature.icon}
                        </Box>

                        <Typography
                          variant="h6"
                          fontWeight={900}
                          sx={{
                            color: "#1d1d1f",
                            fontFamily: "'Red Hat Display', sans-serif",
                            lineHeight: 1.15,
                            letterSpacing: -0.2,
                          }}
                        >
                          {feature.title}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(0,0,0,0.72)",
                            fontFamily: "'Merriweather Sans', sans-serif",
                          }}
                        >
                          {feature.description}
                        </Typography>

                        {/* Links opcionales */}
                        {feature.links?.length > 0 && (
                          <Box
                            sx={{
                              mt: 0.5,
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1.4,
                            }}
                          >
                            {feature.links.map((link, i) => (
                              <Link
                                key={i}
                                component={RouterLink}
                                to={link.href.replace("#", "")}
                                underline="none"
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.6,
                                  color: "#55319b",
                                  fontWeight: 800,
                                  fontFamily: "'Red Hat Display', sans-serif",
                                  borderRadius: 999,
                                  px: 1.2,
                                  py: 0.55,
                                  backgroundColor: "rgba(144,58,242,0.08)",
                                  border: "1px solid rgba(144,58,242,0.16)",
                                  transition: "all .18s ease",
                                  "&:hover": {
                                    color: "#ffffff",
                                    backgroundColor: "#903AF2",
                                    borderColor: "#903AF2",
                                  },
                                }}
                              >
                                {link.text}
                                <ArrowForwardIcon sx={{ fontSize: 18 }} />
                              </Link>
                            ))}
                          </Box>
                        )}

                        {/* mini CTA visual (sin cambiar funcionalidad) */}
                        <Box
                          sx={{
                            mt: "auto",
                            pt: 1.2,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: "rgba(0,0,0,0.55)",
                            fontFamily: "'Red Hat Display', sans-serif",
                            fontWeight: 800,
                          }}
                        >
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: 999,
                              backgroundColor: "#EE964B",
                              boxShadow: "0 0 0 4px rgba(238,150,75,0.18)",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 800 }}
                          >
                            Ver detalle
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </ButtonBase>
                </Box>
              </Fade>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
